const MAX_RECORDING_SECONDS = 120;

const PREFERRED_MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
];

const EXTENSIONS = {
  "audio/webm": ".webm",
  "audio/mp4": ".m4a",
  "audio/x-m4a": ".m4a",
  "audio/ogg": ".ogg",
  "audio/wav": ".wav",
  "audio/mpeg": ".mp3",
};

function baseMimeType(mimeType) {
  if (!mimeType) return "";
  return mimeType.split(";")[0].trim().toLowerCase();
}

export default {
  data() {
    return {
      recording: false,
      elapsedSeconds: 0,
    };
  },
  created() {
    this.mediaStream = null;
    this.mediaRecorder = null;
    this.recordedChunks = [];
    this.timerId = null;
    this.stopResolver = null;
    this.recordingCancelled = false;
  },
  beforeDestroy() {
    if (this.recording) {
      this.cancelRecording();
      return;
    }
    this.releaseRecorder();
  },
  computed: {
    isRecordingSupported() {
      return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia && window.MediaRecorder);
    },
    elapsedLabel() {
      const minutes = Math.floor(this.elapsedSeconds / 60);
      const seconds = this.elapsedSeconds % 60;
      return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    },
  },
  methods: {
    pickMimeType() {
      if (!window.MediaRecorder || !window.MediaRecorder.isTypeSupported) return "";
      return PREFERRED_MIME_TYPES.find((mimeType) => window.MediaRecorder.isTypeSupported(mimeType)) || "";
    },
    recordingFileName(mimeType) {
      return `audio${EXTENSIONS[baseMimeType(mimeType)] || ".webm"}`;
    },
    async startRecording() {
      if (this.recording || !this.isRecordingSupported) return;

      try {
        this.mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true },
        });
      } catch (error) {
        this.notifyRecorderError(error);
        this.releaseRecorder();
        return;
      }

      const mimeType = this.pickMimeType();

      this.recordedChunks = [];
      this.recordingCancelled = false;
      this.mediaRecorder = mimeType
        ? new MediaRecorder(this.mediaStream, { mimeType })
        : new MediaRecorder(this.mediaStream);

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) this.recordedChunks.push(event.data);
      };

      this.mediaRecorder.onstop = () => {
        const resolve = this.stopResolver;
        const chunks = this.recordedChunks;
        const cancelled = this.recordingCancelled;
        const type = this.mediaRecorder ? this.mediaRecorder.mimeType || mimeType : mimeType;

        this.releaseRecorder();

        if (!resolve) return;
        if (cancelled || chunks.length === 0) {
          resolve(null);
          return;
        }

        resolve(new Blob(chunks, { type }));
      };

      this.mediaRecorder.start(1000);
      this.recording = true;
      this.elapsedSeconds = 0;
      this.timerId = setInterval(this.tickRecording, 1000);
    },
    tickRecording() {
      this.elapsedSeconds += 1;
      if (this.elapsedSeconds >= MAX_RECORDING_SECONDS) this.onRecordingLimit();
    },
    onRecordingLimit() {},
    stopRecording() {
      if (!this.recording || !this.mediaRecorder) return Promise.resolve(null);

      this.recording = false;
      this.clearRecordingTimer();

      return new Promise((resolve) => {
        this.stopResolver = resolve;
        this.mediaRecorder.stop();
      });
    },
    cancelRecording() {
      this.recordingCancelled = true;

      if (!this.recording || !this.mediaRecorder) {
        this.releaseRecorder();
        return Promise.resolve(null);
      }

      return this.stopRecording();
    },
    clearRecordingTimer() {
      if (!this.timerId) return;
      clearInterval(this.timerId);
      this.timerId = null;
    },
    releaseRecorder() {
      this.clearRecordingTimer();

      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach((track) => track.stop());
        this.mediaStream = null;
      }

      this.mediaRecorder = null;
      this.recordedChunks = [];
      this.stopResolver = null;
      this.recording = false;
      this.elapsedSeconds = 0;
    },
    notifyRecorderError(error) {
      const name = error && error.name;

      if (name === "NotAllowedError" || name === "SecurityError") {
        this.$toasted.show("Permissão de microfone negada. Libere o microfone nas configurações do navegador.", {
          type: "error",
        });
        return;
      }

      if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        this.$toasted.show("Nenhum microfone encontrado.", { type: "error" });
        return;
      }

      this.$toasted.show("Não foi possível iniciar a gravação.", { type: "error" });
    },
  },
};
